import { type RouteTypes } from '@energovizor/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import userSelector from '@/store/selectors/userSelector';
import { apiClient } from './apiClient';
import { useGetActiveMunicipality } from './getMunicipality';

type InspectionAlertsResponse = RouteTypes['/buildings/inspection-alerts']['get']['response'];
export type InspectionAlert = NonNullable<InspectionAlertsResponse['payload']>[number];

type DismissInspectionAlertBody = RouteTypes['/buildings/inspection-alerts/:inspectionId/dismiss']['patch']['body'];
type DismissInspectionAlertParams = RouteTypes['/buildings/inspection-alerts/:inspectionId/dismiss']['patch']['params'];

export const inspectionAlertsQueryKey = (municipalityId?: string | null, buildingId?: string) => {
  return ['inspection-alerts', municipalityId ?? null, buildingId ?? 'all'] as const;
};

export const useGetInspectionAlerts = ({
  buildingId,
  enabled = true,
}: {
  buildingId?: string;
  enabled?: boolean;
} = {}) => {
  const user = useSelector(userSelector);
  const municipalityId = useGetActiveMunicipality({ enabled });

  return useQuery({
    queryKey: inspectionAlertsQueryKey(municipalityId, buildingId),
    enabled: enabled && !!municipalityId && !!user && user.type === 'client' && !!user.verifiedEmail && !!user.verifiedByAdmin,
    queryFn: async () => {
      const response = await apiClient.get('/buildings/inspection-alerts', {
        params: {
          buildingId,
        },
      });

      return (response.data as InspectionAlertsResponse).payload ?? [];
    },
  });
};

export const useDismissInspectionAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      inspectionId,
      dueDate,
    }: {
      inspectionId: DismissInspectionAlertParams['inspectionId'];
      dueDate: DismissInspectionAlertBody['dueDate'];
    }) => {
      await apiClient.patch('/buildings/inspection-alerts/:inspectionId/dismiss', {
        dueDate,
      }, {
        params: { inspectionId }
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['inspection-alerts'] });
    },
  });
};
